import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const BRAND_STORAGE_KEY = "intern-workspace-brand";

interface BrandInfo {
  id: string;
  displayName: string;
}

interface BrandWorkspaceContextValue {
  selectedBrandId: string | null;
  selectedBrandName: string | null;
  setSelectedBrand: (brand: BrandInfo | null) => void;
  clearSelectedBrand: () => void;
}

const BrandWorkspaceContext = createContext<BrandWorkspaceContextValue | null>(
  null
);

function getStoredBrand(): BrandInfo | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(BRAND_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.id && parsed.displayName) {
        return parsed as BrandInfo;
      }
    }
  } catch {
    // Invalid JSON, clear it
    localStorage.removeItem(BRAND_STORAGE_KEY);
  }
  return null;
}

export function BrandWorkspaceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [selectedBrand, setSelectedBrandState] = useState<BrandInfo | null>(
    () => getStoredBrand()
  );

  // Persist selection to localStorage
  useEffect(() => {
    if (selectedBrand) {
      localStorage.setItem(BRAND_STORAGE_KEY, JSON.stringify(selectedBrand));
    } else {
      localStorage.removeItem(BRAND_STORAGE_KEY);
    }
  }, [selectedBrand]);

  const setSelectedBrand = useCallback((brand: BrandInfo | null) => {
    setSelectedBrandState(brand);
  }, []);

  const clearSelectedBrand = useCallback(() => {
    setSelectedBrandState(null);
  }, []);

  return (
    <BrandWorkspaceContext.Provider
      value={{
        selectedBrandId: selectedBrand?.id ?? null,
        selectedBrandName: selectedBrand?.displayName ?? null,
        setSelectedBrand,
        clearSelectedBrand,
      }}
    >
      {children}
    </BrandWorkspaceContext.Provider>
  );
}

export function useBrandWorkspace() {
  const context = useContext(BrandWorkspaceContext);
  if (!context) {
    throw new Error(
      "useBrandWorkspace must be used within a BrandWorkspaceProvider"
    );
  }
  return context;
}

export default BrandWorkspaceContext;
